import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import logo from "../../images/logo.png";
import { FaCheck } from "react-icons/fa";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";

/** ✅ Move IDS outside the component to silence the dependency warning. */
const IDS = {
  signed_release: 21, // t_key='signed_release'  (confirm with DB)
  consent_history: 24,
  summary_of_rights_under_fcra: 55,
  background_check_fcra: 58,
};

const KEYS = {
  background_check_consent: "consent_to_background_checks",
  psp_disclosure_authorization: "psp_disclosure_authorization",
};

const SignedRelease = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const effectiveUserId = isAdmin && paramId ? paramId : loggedInId;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [user, setUser] = useState(null);
  const [consents, setConsents] = useState([]);
  const [taskStatus, setTaskStatus] = useState({});
  const [signDates, setSignDates] = useState({});

  const isSignedStatus = (status) =>
    status === "review" || status === "completed";

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [userRes, historyRes, taskRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/users/${effectiveUserId}`, { headers }),
          axios.get(`${BASE_URL}/api/work-history/working/${effectiveUserId}`, {
            headers,
          }),
          axios.get(`${BASE_URL}/api/usertasks/status/${effectiveUserId}`, {
            headers,
          }),
        ]);

        setUser(userRes.data);
        setConsents(historyRes.data);
        setTaskStatus(taskRes.data.status || {});
        setSignDates(taskRes.data.signdates || {});
      } catch (err) {
        console.error("Error fetching signed release data", err);
      }
    };

    fetchAll();
  }, [effectiveUserId, BASE_URL]); // ✅ no IDS.* in deps

  const handleClick = (type, whId) => {
    const slideState = { backgroundLocation: location };

    if (type === "background") {
      navigate(
        `/slide/task/${KEYS.background_check_consent}/${effectiveUserId}`,
        {
          state: slideState,
        },
      );
    } else if (type === "psp") {
      navigate(
        `/slide/task/${KEYS.psp_disclosure_authorization}/${effectiveUserId}`,
        {
          state: slideState,
        },
      );
    } else if (type === "fcra") {
      navigate(`/slide/background-check-fcra/${effectiveUserId}`, {
        state: slideState,
      });
    } else if (type === "summary_rights") {
      navigate(`/slide/task/summary_of_rights_under_fcra/${effectiveUserId}`, {
        state: slideState,
      });
    } else if (type === "consent") {
      navigate(`/slide/consent/${effectiveUserId}/${whId}`, {
        state: slideState,
      });
    }
  };

  const renderStatus = (taskKey, taskId = null) => {
    const idKey = taskId ? String(taskId) : null;

    const status =
      (idKey && taskStatus?.[idKey]) ??
      taskStatus?.[taskKey] ??
      "not_completed";

    const isDone = isSignedStatus(status);

    const date = (idKey && signDates?.[idKey]) ?? signDates?.[taskKey] ?? null;

    return (
      <>
        {isDone && <FaCheck color="green" />}
        <span>
          {isDone
            ? `Document signed${date ? ` on ${moment(date).format("ll")}` : ""}`
            : "Please click and sign *"}
        </span>
      </>
    );
  };

  const hasAssignedTask = (taskKey, taskId = null) => {
    const idKey = taskId ? String(taskId) : null;

    return Boolean(
      (idKey && Object.prototype.hasOwnProperty.call(taskStatus, idKey)) ||
        (taskKey && Object.prototype.hasOwnProperty.call(taskStatus, taskKey)),
    );
  };

  const showSummaryOfRights = hasAssignedTask(
    "summary_of_rights_under_fcra",
    IDS.summary_of_rights_under_fcra,
  );
  const showBackgroundCheckFcra = hasAssignedTask(
    "background_check_fcra",
    IDS.background_check_fcra,
  );
  const showPspDisclosure = hasAssignedTask(
    KEYS.psp_disclosure_authorization,
  );

  const requiredTaskStatuses = [
    taskStatus?.[KEYS.background_check_consent],
    ...consents.map((c) => taskStatus?.[`consent_history_${c.wh_id}`]),
  ];

  if (showPspDisclosure) {
    requiredTaskStatuses.push(taskStatus?.[KEYS.psp_disclosure_authorization]);
  }

  if (showBackgroundCheckFcra) {
    requiredTaskStatuses.push(
      taskStatus?.[String(IDS.background_check_fcra)] ??
        taskStatus?.background_check_fcra,
    );
  }

  if (showSummaryOfRights) {
    requiredTaskStatuses.push(
      taskStatus?.[String(IDS.summary_of_rights_under_fcra)] ??
        taskStatus?.summary_of_rights_under_fcra,
    );
  }

  const allConsentTasksSigned =
    requiredTaskStatuses.length > 0 &&
    requiredTaskStatuses.every((status) => isSignedStatus(status));

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3 px-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Pg. 4 - Signed Release
        </span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div
        className="form-wrapper my-4"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: 150, height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Pg. 4 - Signed Release</h2>
          </div>
          <CompanyAddress />
        </div>

        <h5 className="mb-3">Consent Documents</h5>
        {user && (
          <div className="mb-4" style={{ fontSize: "16px", fontWeight: 500 }}>
            Driver: {user.first_name} {user.last_name}
          </div>
        )}

        <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
          Click on a row to view the consent document. Click on the
          checkbox(es), and save this page, to digitally sign the consent(s).
        </p>

        <div className="list-group mb-2">
          <div
            className="list-group-item d-flex justify-content-between align-items-center clickable"
            onClick={() => handleClick("background")}
          >
            <div>* Consent to Background Checks</div>
            <div>{renderStatus(KEYS.background_check_consent)}</div>
          </div>

          {showSummaryOfRights && (
            <div
              className="list-group-item d-flex justify-content-between align-items-center clickable"
              onClick={() => handleClick("summary_rights")}
            >
              <div>* Summary of Rights Under FCRA</div>
              <div>
                {renderStatus(
                  "summary_of_rights_under_fcra",
                  IDS.summary_of_rights_under_fcra,
                )}
              </div>
            </div>
          )}

          {showPspDisclosure && (
            <div
              className="list-group-item d-flex justify-content-between align-items-center clickable"
              onClick={() => handleClick("psp")}
            >
              <div>* PSP Disclosure & Authorization</div>
              <div>{renderStatus(KEYS.psp_disclosure_authorization)}</div>
            </div>
          )}

          {showBackgroundCheckFcra && (
            <div
              className="list-group-item d-flex justify-content-between align-items-center clickable"
              onClick={() => handleClick("fcra")}
            >
              <div>* Background Check Authorization & Disclosure (FCRA)</div>
              <div>
                {renderStatus(
                  "background_check_fcra",
                  IDS.background_check_fcra,
                )}
              </div>
            </div>
          )}

          {consents.map((c) => {
            const key = `consent_history_${c.wh_id}`;
            return (
              <div
                key={c.wh_id}
                className="list-group-item d-flex justify-content-between align-items-center clickable"
                onClick={() => handleClick("consent", c.wh_id)}
              >
                <div>
                  * Consent to Safety and Performance History
                  <span className="ms-2">
                    <br />
                    {c.wh_name}
                  </span>
                </div>
                <div>{renderStatus(key)}</div>
              </div>
            );
          })}
        </div>

        <h6 className="mt-5 mb-2">
          Employment Application Disclaimer and Acknowledgement
        </h6>
        <p className="small">
          I certify that this application was completed by me and that all
          entries on it and information in it are true and complete to the best
          of my knowledge. I understand that any misstatement or omission of
          information in this application may result in my dismissal.
        </p>

        <hr className="my-5" />

        {effectiveUserId && allConsentTasksSigned && (
          <>
            <SignatureManager
              taskName="signed_release"
              taskId={IDS.signed_release}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="signed_release"
              taskId={IDS.signed_release}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}

        {effectiveUserId && !allConsentTasksSigned && (
          <div className="alert alert-info mt-4">
            The final Signed Release signature will appear after all consent
            documents above have been signed.
          </div>
        )}
      </div>
    </>
  );
};

export default SignedRelease;
